// Service-worker registration + update prompt (docs/06-clients.md §4:
// "update prompt on new deploy"). The flow:
//
//   register → browser finds a byte-different sw.js → new worker installs →
//   reaches "installed" while an old one controls the page → we publish
//   `updateReady`; the shell shows a prompt → applyUpdate() posts
//   SKIP_WAITING → the new worker activates → controllerchange → reload.
//
// Also the window-side receiver for SW messages (pending-invite pickup on
// notification tap, push-rotate requests). All browser surfaces are injected
// for unit testing.

import { Store, type ReadableStore } from '@merkury/brain/call/store.js';
import { SKIP_WAITING } from './messages.js';

// --- Injected platform surfaces -----------------------------------------------

export interface SwLike {
  readonly state: string;
  postMessage(message: unknown): void;
  addEventListener(type: 'statechange', listener: () => void): void;
}

export interface SwRegistrationLike {
  readonly installing: SwLike | null;
  readonly waiting: SwLike | null;
  addEventListener(type: 'updatefound', listener: () => void): void;
  update(): Promise<unknown>;
}

export interface SwContainerLike {
  readonly controller: unknown | null;
  register(url: string, options?: { type?: 'module' | 'classic' }): Promise<SwRegistrationLike>;
  addEventListener(type: 'controllerchange' | 'message', listener: (ev: { data?: unknown }) => void): void;
}

// --- Published state --------------------------------------------------------------

export interface SwState {
  status: 'unsupported' | 'registering' | 'registered' | 'failed';
  /** A new version is installed and waiting — show the update prompt. */
  updateReady: boolean;
}

export interface SwControllerDeps {
  /** `navigator.serviceWorker`, or undefined when unsupported. */
  container: SwContainerLike | undefined;
  /** Reload the page after the new worker takes control. */
  reload: () => void;
  /** SW script URL; vite-plugin-pwa emits /sw.js at the site root. */
  url?: string;
  /** Receives messages posted by the SW (pending invite, push rotate). */
  onMessage?: (data: unknown) => void;
}

export class SwController {
  readonly state: ReadableStore<SwState> & Store<SwState>;

  private readonly deps: SwControllerDeps;
  private registration: SwRegistrationLike | undefined;
  private reloading = false;

  constructor(deps: SwControllerDeps) {
    this.deps = deps;
    this.state = new Store<SwState>({
      status: deps.container ? 'registering' : 'unsupported',
      updateReady: false,
    });
  }

  async register(): Promise<void> {
    const container = this.deps.container;
    if (!container) return;

    container.addEventListener('message', (ev) => this.deps.onMessage?.(ev.data));
    container.addEventListener('controllerchange', () => {
      // Fires when the waiting worker we told to SKIP_WAITING activates.
      // Guarded: it also fires on first-install claim, where reloading would
      // be a pointless flash.
      if (!this.reloading) return;
      this.deps.reload();
    });

    try {
      const registration = await container.register(this.deps.url ?? '/sw.js');
      this.registration = registration;

      // A worker may already be waiting (page reopened mid-update).
      if (registration.waiting && container.controller) {
        this.state.patch({ updateReady: true });
      }
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && container.controller) {
            this.state.patch({ updateReady: true });
          }
        });
      });

      this.state.patch({ status: 'registered' });
    } catch {
      this.state.patch({ status: 'failed' });
    }
  }

  /** Check for a new deploy now (e.g. on visibilitychange → visible). */
  async checkForUpdate(): Promise<void> {
    await this.registration?.update().catch(() => undefined);
  }

  /** User accepted the update prompt: activate the waiting worker + reload. */
  applyUpdate(): void {
    const waiting = this.registration?.waiting;
    if (!waiting) return;
    this.reloading = true;
    waiting.postMessage({ type: SKIP_WAITING });
  }
}
